import { memo, useEffect } from 'react'
import { useUpdateNodeInternals } from '@xyflow/react'
import DialpadIcon from '@mui/icons-material/Dialpad'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import QuizIcon from '@mui/icons-material/Quiz'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import AbcIcon from '@mui/icons-material/Abc'
import BaseNode from './BaseNode'
import { WorkflowNodeData } from '@store/workflowStore'
import { WorkflowNodeType } from '@features/adventures/types/workflow.types'

interface ChallengeNodeProps {
  id: string
  data: WorkflowNodeData
  selected?: boolean
}

/**
 * ChallengeNode - Handles all challenge node types
 * - QuestionAnswer (nodeType: 6)
 * - QRCode (nodeType: 7)
 * - NumericInput (nodeType: 8)
 * - ModeratorApproval (nodeType: 9)
 */
function ChallengeNode(props: ChallengeNodeProps) {
  const { id, data } = props
  const updateNodeInternals = useUpdateNodeInternals()
  const { nodeType } = data

  // Determine node type
  const isNumpadNode = nodeType === WorkflowNodeType.NumericInput
  const isQrScannerNode = nodeType === WorkflowNodeType.QRCode
  const isQuestionAnswerNode = nodeType === WorkflowNodeType.QuestionAnswer
  const isModeratorApprovalNode = nodeType === WorkflowNodeType.ModeratorApproval
  const isCodexNode = nodeType === WorkflowNodeType.CodexInput

  // Get configuration from appropriate config object
  const acceptedNumbers = data.numpadConfig?.acceptedNumbers ?? []
  const acceptedCodes = data.codexConfig?.acceptedCodes ?? []
  const sharedCodes = data.qrCodeConfig?.sharedCodes ?? []
  const allowSkip = data.questionAnswerConfig?.allowSkip ?? false

  // Force React Flow to re-register handles when config changes
  useEffect(() => {
    // Small delay to ensure DOM is updated before re-registering handles
    const timer = setTimeout(() => {
      updateNodeInternals(id)
    }, 0)
    return () => clearTimeout(timer)
  }, [id, acceptedNumbers.length, acceptedCodes.length, sharedCodes.length, allowSkip, updateNodeInternals])

  // Determine source handle count based on node type
  let sourceHandleCount = 1
  let sourceHandleLabels: string[] | undefined
  let showSkipHandle = false

  if (isNumpadNode) {
    // Number pad: handles for each accepted number (min 1)
    sourceHandleCount = Math.max(1, acceptedNumbers.length)
    sourceHandleLabels = acceptedNumbers.length > 0 ? acceptedNumbers : undefined
  } else if (isQrScannerNode) {
    // QR scanner: handles for each shared code (min 1)
    sourceHandleCount = Math.max(1, sharedCodes.length)
    sourceHandleLabels = sharedCodes.length > 0 ? sharedCodes : undefined
  } else if (isQuestionAnswerNode) {
    // Q/A: "Correct" exit + optional "Skip" exit
    sourceHandleCount = 1
    sourceHandleLabels = ['Correct']
    showSkipHandle = allowSkip
  } else if (isModeratorApprovalNode) {
    // Moderator approval: approved, denied, and timeout exits
    sourceHandleCount = 3
    sourceHandleLabels = ['Approved', 'Denied', 'Timeout']
  } else if (isCodexNode) {
    // Codex: handles for each accepted code/word (min 1)
    sourceHandleCount = Math.max(1, acceptedCodes.length)
    sourceHandleLabels = acceptedCodes.length > 0 ? acceptedCodes : undefined
  }

  // Determine icon and header label based on node type
  let icon = <QuizIcon fontSize="small" />
  let headerLabel = 'Challenge'

  if (isNumpadNode) {
    icon = <DialpadIcon fontSize="small" />
    headerLabel = 'Number Pad'
  } else if (isQrScannerNode) {
    icon = <QrCodeScannerIcon fontSize="small" />
    headerLabel = 'QR Scanner'
  } else if (isCodexNode) {
    icon = <AbcIcon fontSize="small" />
    headerLabel = 'Codex'
  } else if (isQuestionAnswerNode) {
    icon = <QuizIcon fontSize="small" />
    headerLabel = 'Question'
  } else if (isModeratorApprovalNode) {
    icon = <AdminPanelSettingsIcon fontSize="small" />
    headerLabel = 'Moderator Approval'
  }

  return (
    <BaseNode
      {...props}
      icon={icon}
      showTargetHandle={true}
      showSourceHandle={true}
      sourceHandleCount={sourceHandleCount}
      sourceHandleLabels={sourceHandleLabels}
      showCancelHandle={isNumpadNode || isCodexNode || isQrScannerNode || (isQuestionAnswerNode && allowSkip)}
      showSkipHandle={showSkipHandle}
      showFailedHandle={isNumpadNode || isCodexNode}
      showIncorrectHandle={isQuestionAnswerNode}
      headerLabel={headerLabel}
      hideConditionChip={true}
    />
  )
}

export default memo(ChallengeNode)
